// =================================================================================================
// >>> **AUTH-BREACH-1 — THE BOX THE CLAIM LINK OPENS.** <<<
//
// Her order: *"the link opens the create-your-password box, they set it, and from then on they sign
// in with email+password."* This is that box, and it is a STANDALONE page for `/funnel`'s own reason
// — a member who arrived here from her inbox is doing one thing, and a side menu is eleven ways to
// abandon it.
//
// **THE TOKEN NEVER TOUCHES REACT STATE THAT SURVIVES THE SUBMIT**, and it is stripped from the URL
// bar the moment the page mounts: a password-claim link sitting in a browser history, a screenshot or
// a shoulder-surfed address bar is the secret leaking through the one channel this whole round exists
// to close.
// =================================================================================================
import { useEffect, useRef, useState, type FormEvent } from "react";
import { PASSWORD_MIN, passwordProblem, passwordHint } from "../accountPassword";

type Member = { name: string; email: string; subscribed: boolean; plan: string };

export function ClaimPage({ onMemberChange }: { onMemberChange: (m: Member) => void }) {
  const token = useRef<string>("");
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [done, setDone] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const found = new URLSearchParams(window.location.search).get("token") || "";
    token.current = found;
    // >>> **STRIPPED FROM THE ADDRESS BAR IMMEDIATELY**, kept only in a ref. `replaceState` so the
    // back button does not walk her onto the bare token again.
    if (found) window.history.replaceState({}, "", "/claim");
    setReady(true);
  }, []);

  // The strength bar runs as she types, but it only SPEAKS once she has typed something real — a box
  // that shouts "too short" at the first keystroke is a box that reads as broken.
  const problem = password ? passwordProblem(password, {}) : "";
  const mismatch = again.length > 0 && password !== again;
  const maySubmit = !busy && !problem && password.length > 0 && password === again;

  async function submit(event?: FormEvent) {
    if (event) event.preventDefault();
    if (inFlight.current || !maySubmit) return;
    inFlight.current = true;
    setBusy(true);
    setStatus("");
    try {
      const response = await fetch("/api/auth/claim/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: token.current, password }),
      });
      const data = await response.json();
      if (!response.ok || !data?.ok) throw new Error(data?.message || "That did not go through. Try once more.");
      setDone(true);
      // Wiped the instant it is spent: nothing keeps the secret in memory after the answer.
      setPassword(""); setAgain(""); token.current = "";
      if (data.member) onMemberChange(data.member);
      window.setTimeout(() => { window.location.href = "/my-room"; }, 1400);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "That did not go through. Try once more.");
    } finally {
      // A guard that never reopens is a dead control, not a safe one (D-5).
      inFlight.current = false;
      setBusy(false);
    }
  }

  if (!ready) return <div className="claim-page" />;

  if (!token.current && !done) {
    return (
      <div className="claim-page">
        <div className="claim-card">
          <h1 className="claim-title">This link is incomplete</h1>
          <p className="claim-line">
            Open the button in the email we sent you, or send yourself a fresh link from the sign-in page.
            Links work for one hour.
          </p>
          <a className="claim-submit" href="/signup">Go to sign-in</a>
        </div>
      </div>
    );
  }

  return (
    <div className="claim-page">
      <div className="claim-card">
        {done ? (
          <>
            <h1 className="claim-title">You are all set</h1>
            <p className="claim-line">
              Your account is yours alone now. Taking you to your room — everything is exactly where you left it.
            </p>
          </>
        ) : (
          <>
            <h1 className="claim-title">Set your password</h1>
            <p className="claim-line">
              Sign-in at 12th House just got stronger. Choose a password and from now on it is you and only you.
            </p>
            <form onSubmit={submit} noValidate>
              <label className="claim-label" htmlFor="claim-pw">Your new password</label>
              <input
                id="claim-pw"
                className="claim-input"
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
              <label className="claim-label" htmlFor="claim-pw2">Type it once more</label>
              <input
                id="claim-pw2"
                className="claim-input"
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={again}
                onChange={(e) => setAgain(e.target.value)}
                disabled={busy}
              />
              <button type="button" className="claim-show" onClick={() => setShow((v) => !v)}>
                {show ? "Hide password" : "Show password"}
              </button>
              <p className="claim-hint">
                {status
                  || (problem ? passwordHint(problem) : "")
                  || (mismatch ? "Those two do not match yet." : `${PASSWORD_MIN} characters or more. A short phrase you will remember beats a clever word.`)}
              </p>
              <button className="claim-submit" type="submit" disabled={!maySubmit}>
                {busy ? "Setting it…" : "Secure my account"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
